/**
 * How far along an attachment's upload is — and nothing else.
 *
 * This is a separate module from `attachmentStorage.ts` on purpose. That one
 * imports `firebase/auth`, which touches global `fetch` AT IMPORT TIME; three
 * chat-isolation suites that render <ChatAttachment> have no `fetch` in their
 * environment and failed to load the moment the tile imported the uploader
 * (2026-09-11). Mocking `firebase/auth` in each of them would have hidden the
 * real point: a tile that wants to show a percentage should depend on a
 * PROGRESS READER, not on the thing that performs uploads.
 *
 * Keyed by File, and a WeakMap because it is only ever read by File identity
 * and never enumerated: a removed attachment's entry dies with the File, so
 * there is no cleanup path to forget to call.
 */
const progressByFile = new WeakMap<File, number>();

/** Record progress for a file currently uploading. Fraction, 0..1. */
export function setUploadProgress(file: File, fraction: number): void {
  if (!file || !Number.isFinite(fraction)) return;
  progressByFile.set(file, Math.max(0, Math.min(1, fraction)));
}

/**
 * Fraction uploaded (0..1), or undefined when this lane cannot report it —
 * the CF proxy lane sends the File through `fetch`, which has no progress
 * events, so "undefined" is the honest answer there and the caller shows the
 * label it always showed.
 */
export function getUploadProgress(file: File | undefined | null): number | undefined {
  return file ? progressByFile.get(file) : undefined;
}
